// Shape management module for PPT Hero game
export default class ShapeManager {
  constructor(scene) {
    this.scene = scene;
  }

  // Create a new shape with specified properties
  createShape(x, y, w, h, color, isFixed = false, isDecoration = false, shapeType = 'rectangle') {
    try {
      let shape;
      
      // Create different shape types based on shapeType parameter
      switch (shapeType) {
        case 'triangle':
          shape = this.scene.add.triangle(x, y, 0, h, w/2, 0, w, h, color);
          break;
        case 'circle':
          // Use the larger dimension as radius
          const radius = Math.max(w, h) / 2;
          shape = this.scene.add.circle(x, y, radius, color);
          break;
        case 'square':
          // For squares, use the average of w and h to ensure it's actually square
          const size = Math.max(w, h);
          shape = this.scene.add.rectangle(x, y, size, size, color);
          break;
        case 'rectangle':
        default:
          shape = this.scene.add.rectangle(x, y, w, h, color);
          break;
      }
      
      // Set up shape properties
      shape.setInteractive({ draggable: !isFixed });
      shape.setStrokeStyle(2, 0x000000, 0.8);
      shape.setDepth(200);
      
      // Add custom properties
      shape.isFixed = isFixed;
      shape.isDecoration = isDecoration;
      shape.isSelected = false;
      shape.fillColor = color; // Store the original color for level goal checking
      shape.originalFillColor = color; // Store original color for selection
      
      // Add drag events if not fixed
      if (!isFixed) {
        shape.on('dragstart', (pointer, dragX, dragY) => {
          this.onDragStart(pointer, shape);
        });
        
        shape.on('drag', (pointer, dragX, dragY) => {
          this.onDrag(pointer, shape, dragX, dragY);
        });
        
        shape.on('dragend', (pointer) => {
          this.onDragEnd(pointer, shape);
        });
      }
      
      // PowerPoint-style selection (now working!)
      shape.on('pointerdown', (pointer) => {
        try {
          // Check keyboard state
          const isShiftHeld = this.scene.input.keyboard.checkDown(this.scene.input.keyboard.addKey('SHIFT'));
          const isCtrlHeld = this.scene.input.keyboard.checkDown(this.scene.input.keyboard.addKey('CTRL'));
          
          // Initialize array if not exists
          if (!this.scene.selectedShapes) {
            this.scene.selectedShapes = [];
          }
          
          console.log('🎯 Shape clicked:', { shift: isShiftHeld, ctrl: isCtrlHeld, currentSelected: this.scene.selectedShapes.length });
          
          // Multi-select behavior (Shift or Ctrl held)
          if (isShiftHeld || isCtrlHeld) {
            console.log('🔧 Multi-select mode');
            
            // Toggle shape in selection
            const index = this.scene.selectedShapes.indexOf(shape);
            if (index > -1) {
              // Remove from selection
              this.scene.selectedShapes.splice(index, 1);
              shape.setStrokeStyle(2, 0x000000, 0.8);
              console.log('🔧 Shape removed from selection');
            } else {
                          // Add to selection  
            this.scene.selectedShapes.push(shape);
            shape.setStrokeStyle(2, 0xff0000); // Match CTRL+A style
            console.log('🔧 Shape added to selection');
            }
          } else {
            console.log('🔧 Single select mode - clearing others');
            
            // Clear all existing selections first
            this.scene.selectedShapes.forEach(s => {
              if (s && s.setStrokeStyle) {
                s.setStrokeStyle(2, 0x000000, 0.8);
              }
            });
            
                      // Select only this shape
          this.scene.selectedShapes = [shape];
          shape.setStrokeStyle(2, 0xff0000); // Match CTRL+A style
          console.log('🔧 Single shape selected');
          }
          
          console.log(`🎯 Selection complete. Total selected: ${this.scene.selectedShapes.length}`);
          
          // DEBUG: Check if all selected shapes actually have red borders
          console.log('🔍 Checking borders on all selected shapes:');
          this.scene.selectedShapes.forEach((s, i) => {
            console.log(`  Shape ${i}: stroke color=${s.strokeColor}, stroke width=${s.lineWidth}`);
          });
          
        } catch (error) {
          console.error('🚨 ERROR in selection:', error);
        }
      });
      
      return shape;
    } catch (error) {
      console.error('Error creating shape:', error);
      return null;
    }
  }

  // Select a shape (clears other selections first)
  selectShape(shape) {
    if (!shape || shape.isDecoration) return;
    
    // Deselect all other shapes
    this.scene.clearSelection();
    
    // Add this shape to selection
    this.addToSelection(shape);
  }

  // Add a shape to selection without clearing others
  addToSelection(shape) {
    if (!shape || shape.isDecoration) return;
    if (this.scene.selectedShapes.includes(shape)) return; // Already selected
    
    // Select this shape
    shape.isSelected = true;
    shape.setStrokeStyle(3, 0xff0000, 1.0); // RED border for consistency with Ctrl+A
    // Store original color for restoration
    if (!shape.originalFillColor) {
      shape.originalFillColor = shape.fillColor;
    }
    
    // Add to selected shapes array
    this.scene.selectedShapes.push(shape);
    
    // Update selection count display
    this.scene.selectionManager.updateSelectionCount();
  }

  // Deselect a specific shape
  deselectShape(shape) {
    if (!shape) return;
    
    shape.isSelected = false;
    shape.setStrokeStyle(2, 0x000000, 0.8); // Default black border
    // Restore original color
    if (shape.originalFillColor !== undefined) {
      shape.setFillStyle(shape.originalFillColor, 1.0);
    }
    
    // Remove from selected shapes array
    const index = this.scene.selectedShapes.indexOf(shape);
    if (index > -1) {
      this.scene.selectedShapes.splice(index, 1);
    }
    
    // Update selection count display
    this.scene.selectionManager.updateSelectionCount();
  }

  // Clear all selections
  clearSelection() {
    this.scene.selectedShapes.forEach(shape => {
      if (shape && shape.setStrokeStyle) {
        shape.setStrokeStyle(2, 0x000000, 0.8); // Default black border
        // Restore original color
        if (shape.originalFillColor !== undefined) {
          shape.setFillStyle(shape.originalFillColor, 1.0);
        }
        shape.isSelected = false;
      }
    });
    this.scene.selectedShapes = [];
    
    // Update selection count display
    this.scene.selectionManager.updateSelectionCount();
  }

  // Handle pointer movement for shape selection
  onPointerMove(pointer) {
    if (this.scene.isTransitioning) return;
    
    // Update cursor based on what's under the pointer
    const objectsUnderPointer = this.scene.input.hitTestPointer(pointer);
    let cursorChanged = false;
    
    for (const obj of objectsUnderPointer) {
      if (obj.isInteractive && !obj.isFixed) {
        this.scene.input.setDefaultCursor('pointer');
        cursorChanged = true;
        break;
      }
    }
    
    if (!cursorChanged) {
      this.scene.input.setDefaultCursor('default');
    }
  }

  // Handle pointer up events
  onPointerUp() {
    if (this.scene.isTransitioning) return;
    
    // Reset cursor
    this.scene.input.setDefaultCursor('default');
  }

  // Handle drag start
  onDragStart(pointer, gameObject) {
    if (this.scene.isTransitioning || gameObject.isFixed) return;
    
    // Set dragging state
    this.scene.isDragging = true;
    
    // Visual feedback
    gameObject.setAlpha(0.8);
    
    // Track analytics
    this.scene.trackKeystrokes();
  }

  // Handle drag movement
  onDrag(pointer, gameObject, dragX, dragY) {
    if (this.scene.isTransitioning || gameObject.isFixed) return;
    
    // Update position
    gameObject.x = dragX;
    gameObject.y = dragY;
    
    // Track analytics
    this.scene.trackKeystrokes();
  }

  // Handle drag end
  onDragEnd(pointer, gameObject) {
    if (this.scene.isTransitioning) return;
    
    // Reset dragging state
    this.scene.isDragging = false;
    
    // Visual feedback
    gameObject.setAlpha(1);
    
    // Track analytics
    this.scene.trackKeystrokes();
  }

  // Create level shapes
  createLevelShapes() {
    console.log('🚨 ShapeManager.createLevelShapes() called - this should be delegated to LevelManager!');
    
    try {
      // Clear existing shapes
      if (this.scene.levelShapes) {
        this.scene.levelShapes.forEach(shape => shape.destroy());
      }
      if (this.scene.allShapes) {
        this.scene.allShapes.forEach(shape => shape.destroy());
      }
      
      // Initialize arrays
      this.scene.levelShapes = [];
      this.scene.allShapes = [];
      this.scene.selectedShapes = [];
      
      // Safety check for levels array
      if (!this.scene.levels || !Array.isArray(this.scene.levels)) {
        console.warn('Levels array not initialized, creating default practice shapes');
        this.createDefaultShapes();
        return;
      }
      
      // Get current level data - ALWAYS find by ID, not array index (Bain levels have different indices)
      let levelData = this.scene.levels.find(level => level.id === this.scene.currentLevel);
      if (!levelData) {
        // For backwards compatibility, try array index for levels 0-10 only
        if (this.scene.currentLevel >= 0 && this.scene.currentLevel <= 10) {
          levelData = this.scene.levels[this.scene.currentLevel];
        }
      }
      
      if (!levelData || !levelData.shapes) {
        console.warn('No shapes data for level:', this.scene.currentLevel, 'creating default shapes');
        this.createDefaultShapes();
        return;
      }
      
      console.log('📊 ShapeManager creating shapes for level:', this.scene.currentLevel, levelData.shapes);
      
      // Use the slideArea from the scene if available, otherwise calculate
      let slideLeft, slideTop;
      if (this.scene.slideArea) {
        console.log('Using scene slideArea for level shapes:', this.scene.slideArea);
        slideLeft = this.scene.slideArea.left;
        slideTop = this.scene.slideArea.top;
      } else {
        // Fallback calculation matching the scene's slide positioning
        const sidebarWidth = this.scene.rexUIManager?.sidebarWidth || 250;
        const sidebarGap = this.scene.rexUIManager?.sidebarGap || 20;
        const topBarHeight = 60; // Reduced to raise canvas higher
        const bottomBarHeight = 60; // Reduced to raise canvas higher
        
        const availableWidth = this.scene.cameras.main.width - sidebarWidth - sidebarGap;
        const availableHeight = this.scene.cameras.main.height - topBarHeight - bottomBarHeight;
        const slideWidth = Math.min(availableWidth * 0.85, 800);
        const slideHeight = Math.min(availableHeight * 0.85, 600);
        
        const slideX = sidebarWidth + sidebarGap + (availableWidth / 2);
        const slideY = topBarHeight + (availableHeight / 2) - 20; // Raise canvas by 20px
        
        slideLeft = slideX - slideWidth / 2;
        slideTop = slideY - slideHeight / 2;
      }
      
      // Create shapes from level data using the standardized scene.createShape method
      levelData.shapes.forEach((shapeConfig, index) => {
        console.log(`📊 ShapeManager creating shape ${index}:`, shapeConfig);
        
        const shape = this.scene.createShape(
          slideLeft + shapeConfig.x, 
          slideTop + shapeConfig.y, 
          shapeConfig.w, 
          shapeConfig.h, 
          shapeConfig.color,
          shapeConfig.isFixed || false,
          shapeConfig.isDecoration || false,
          shapeConfig.type || 'rectangle'
        );
        
        // Tag shape with its type for level completion detection
        if (shape) {
          shape.setData('shapeType', shapeConfig.type || 'rectangle');
          console.log(`📊 ShapeManager shape ${index} created:`, { type: shapeConfig.type, x: shape.x, y: shape.y, width: shape.width, height: shape.height });
        }
        
        // Add to arrays
        this.scene.levelShapes.push(shape);
        this.scene.allShapes.push(shape);
      });
      
      console.log('Created', this.scene.allShapes.length, 'shapes');
      
    } catch (error) {
      console.error('Error creating level shapes:', error);
    }
  }

  // Create default shapes for practice or when level data is missing
  createDefaultShapes() {
    console.log('Creating default practice shapes');
    
    try {
      // Use the slideArea from the scene if available, otherwise calculate
      let slideBounds;
      if (this.scene.slideArea) {
        console.log('Using scene slideArea:', this.scene.slideArea);
        slideBounds = {
          left: this.scene.slideArea.left + 30,
          right: this.scene.slideArea.right - 30,
          top: this.scene.slideArea.top + 30,
          bottom: this.scene.slideArea.bottom - 30
        };
      } else {
        // Fallback calculation matching the scene's slide positioning
        const sidebarWidth = this.scene.rexUIManager?.sidebarWidth || 250;
        const sidebarGap = this.scene.rexUIManager?.sidebarGap || 20;
        const topBarHeight = 60; // Reduced to raise canvas higher
        const bottomBarHeight = 60; // Reduced to raise canvas higher
        
        const availableWidth = this.scene.cameras.main.width - sidebarWidth - sidebarGap;
        const availableHeight = this.scene.cameras.main.height - topBarHeight - bottomBarHeight;
        const slideWidth = Math.min(availableWidth * 0.85, 800);
        const slideHeight = Math.min(availableHeight * 0.85, 600);
        
        const slideX = sidebarWidth + sidebarGap + (availableWidth / 2);
        const slideY = topBarHeight + (availableHeight / 2) - 20; // Raise canvas by 20px
        
        slideBounds = {
          left: slideX - slideWidth / 2 + 30,
          right: slideX + slideWidth / 2 - 30,
          top: slideY - slideHeight / 2 + 30,
          bottom: slideY + slideHeight / 2 - 30
        };
      }
      
      console.log('Slide bounds for shape creation:', slideBounds);
      
      // Create some basic practice shapes
      const defaultShapes = [
        { x: slideBounds.left + 100, y: slideBounds.top + 100, width: 80, height: 60, color: 0x3498db },
        { x: slideBounds.left + 200, y: slideBounds.top + 100, width: 80, height: 60, color: 0xe74c3c },
        { x: slideBounds.left + 300, y: slideBounds.top + 100, width: 80, height: 60, color: 0x2ecc71 },
        { x: slideBounds.left + 100, y: slideBounds.top + 200, width: 120, height: 40, color: 0xf39c12 },
        { x: slideBounds.left + 250, y: slideBounds.top + 200, width: 120, height: 40, color: 0x9b59b6 }
      ];
      
      // Create the shapes
      defaultShapes.forEach((shapeData, index) => {
        const shape = this.scene.add.rectangle(
          shapeData.x, 
          shapeData.y, 
          shapeData.width, 
          shapeData.height, 
          shapeData.color
        );
        
        shape.setStrokeStyle(2, 0x000000);
        shape.setInteractive();
        shape.setDepth(100);
        
        // Add shape properties
        shape.shapeId = `default_${index}`;
        shape.isFixed = false;
        shape.isDecoration = false;
        shape.originalFillColor = shapeData.color; // Store original color for selection
        
        // Make it draggable
        this.scene.input.setDraggable(shape);
        
        // Add to arrays
        this.scene.levelShapes.push(shape);
        this.scene.allShapes.push(shape);
      });
      
      console.log(`Created ${defaultShapes.length} default practice shapes`);
      
    } catch (error) {
      console.error('Error creating default shapes:', error);
    }
  }

  /**
   * Clear all shapes from the scene
   */
  clearAllShapes() {
    try {
      // Destroy all level shapes
      if (this.scene.levelShapes) {
        this.scene.levelShapes.forEach(shape => {
          if (shape && shape.destroy) {
            shape.destroy();
          }
        });
      }
      
      // Destroy all shapes
      if (this.scene.allShapes) {
        this.scene.allShapes.forEach(shape => {
          if (shape && shape.destroy) {
            shape.destroy();
          }
        });
      }
      
      // Reset the arrays
      this.scene.levelShapes = [];
      this.scene.allShapes = [];
      this.scene.selectedShapes = [];
      
      console.log('All shapes cleared');
      
    } catch (error) {
      console.error('Error clearing shapes:', error);
    }
  }
}
